import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'

import { STORAGE_KEYS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

/**
 * Theme mode types
 * - 'light': Always use light theme
 * - 'dark': Always use dark theme
 * - 'system': Follow system preference
 */
export type ThemeMode = 'light' | 'dark' | 'system'

/**
 * Theme colors interface for type-safe access to CSS variables
 */
interface ThemeColors {
  background: string
  card: string
  border: string
  text: string
  textSecondary: string
  muted: string
  accent: string
  success: string
  warning: string
  danger: string
}

/**
 * Theme context state
 */
interface ThemeState {
  /** Current theme mode preference */
  mode: ThemeMode
  /** Actual resolved theme (light or dark) */
  resolvedTheme: 'light' | 'dark'
  /** Whether theme is currently transitioning */
  isTransitioning: boolean
  /** Current theme colors from CSS variables */
  colors: ThemeColors
}

/**
 * Theme context actions
 */
interface ThemeActions {
  /** Set theme mode (light/dark/system) */
  setMode: (mode: ThemeMode) => void
  /** Toggle between light and dark mode */
  toggleTheme: () => void
  /** Set light mode */
  setLightMode: () => void
  /** Set dark mode */
  setDarkMode: () => void
  /** Set system mode */
  setSystemMode: () => void
}

/**
 * Complete theme context interface
 */
interface ThemeContextValue extends ThemeState, ThemeActions {}

/**
 * Theme context
 */
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

/**
 * Default theme colors for initial render (dark theme defaults)
 */
const DEFAULT_COLORS: ThemeColors = {
  background: '#0B0D17',
  card: '#161B26',
  border: '#1F2937',
  text: '#F3F4F6',
  textSecondary: '#D1D5DB',
  muted: '#9CA3AF',
  accent: '#6366F1',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
}

/**
 * Local storage key for theme MODE preference (light / dark / system).
 *
 * Distinct from `STORAGE_KEYS.THEME` (the RESOLVED 'light' | 'dark' value read
 * on boot by the global `useTheme` hook + the transaction-matching page). We
 * keep two keys because this one also stores `'system'`, which those consumers
 * do not understand — but they are NOT allowed to diverge: `applyThemeToDOM`
 * mirrors every resolved theme it paints into `STORAGE_KEYS.THEME`, so whichever
 * system wins the boot race resolves to the SAME theme. See STORAGE_KEYS doc
 * comment in @/shared/utils/constants.ts.
 */
const THEME_STORAGE_KEY = STORAGE_KEYS.THEME_PREFERENCE

/**
 * Get theme colors from CSS variables
 */
function getThemeColors(): ThemeColors {
  if (typeof window === 'undefined') {
    return DEFAULT_COLORS
  }

  const root = document.documentElement
  const getVar = (name: string): string => {
    const value = root.style.getPropertyValue(name)
    return value || getComputedStyle(root).getPropertyValue(name).trim()
  }

  return {
    background: getVar('--color-crypto-dark') || DEFAULT_COLORS.background,
    card: getVar('--color-crypto-card') || DEFAULT_COLORS.card,
    border: getVar('--color-crypto-border') || DEFAULT_COLORS.border,
    text: getVar('--color-crypto-text') || DEFAULT_COLORS.text,
    textSecondary: getVar('--text-secondary') || DEFAULT_COLORS.textSecondary,
    muted: getVar('--color-crypto-muted') || DEFAULT_COLORS.muted,
    accent: getVar('--color-crypto-accent') || DEFAULT_COLORS.accent,
    success: getVar('--color-crypto-success') || DEFAULT_COLORS.success,
    warning: getVar('--color-crypto-warning') || DEFAULT_COLORS.warning,
    danger: getVar('--color-crypto-danger') || DEFAULT_COLORS.danger,
  }
}

/**
 * Apply theme to DOM with smooth transition
 */
function applyThemeToDOM(theme: 'light' | 'dark', isTransitioning = false): void {
  if (typeof window === 'undefined') return

  const root = document.documentElement

  // Add transition class for smooth theme changes
  if (isTransitioning) {
    root.classList.add('theme-transition')
  }

  // Remove existing theme classes and attributes
  root.classList.remove('light', 'dark')

  // Add new theme class and data attribute
  root.classList.add(theme)
  root.setAttribute('data-theme', theme)

  // Mirror the RESOLVED theme into STORAGE_KEYS.THEME — the key the global
  // `useTheme` hook (and the transaction-matching page) read on boot. Without
  // this, a theme changed via the navbar toggle / Preferences dropdown (which
  // flow through ThemeProvider and write only THEME_PREFERENCE) would revert on
  // reload, because the boot code reads THEME, not THEME_PREFERENCE.
  try {
    localStorage.setItem(STORAGE_KEYS.THEME, theme)
  } catch (error) {
    logger.warn('[ThemeProvider] Failed to persist resolved theme to localStorage:', error)
  }

  // Remove transition class after animation completes
  if (isTransitioning) {
    setTimeout(() => {
      root.classList.remove('theme-transition')
    }, 300)
  }
}

/**
 * Resolve system theme preference
 */
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'dark' // Default to dark on server
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Get initial theme mode from localStorage or default
 */
function getInitialThemeMode(): ThemeMode {
  if (typeof window === 'undefined') {
    return 'dark' // Default to dark mode
  }

  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored && (stored === 'light' || stored === 'dark' || stored === 'system')) {
      return stored as ThemeMode
    }
    // Legacy alias: old builds persisted 'auto' (removed THEME_OPTIONS entry)
    // for "follow the system preference". Treat it as 'system' instead of
    // silently discarding the user's choice and defaulting to dark.
    if (stored === 'auto') {
      return 'system'
    }
  } catch (error) {
    logger.warn('[ThemeProvider] Failed to read theme from localStorage:', error)
  }

  return 'dark' // Default to dark mode
}

/**
 * ThemeProvider Props
 */
interface ThemeProviderProps {
  /** Initial theme mode (optional, defaults to localStorage or 'dark') */
  initialMode?: ThemeMode
  /** Children components */
  children: ReactNode
}

/**
 * ThemeProvider Component
 *
 * Provides theme context to all child components with:
 * - System preference detection
 * - Manual toggle (light/dark/system)
 * - Persist preference in localStorage
 * - Smooth transitions
 *
 * @example
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ initialMode, children }) => {
  // State for theme mode preference
  const [mode, setModeState] = useState<ThemeMode>(() => initialMode ?? getInitialThemeMode())

  // State for actual resolved theme
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    const initial = initialMode ?? getInitialThemeMode()
    if (initial === 'system') {
      return getSystemTheme()
    }
    return initial
  })

  // State for transition animation
  const [isTransitioning, setIsTransitioning] = useState(false)

  // State for theme colors
  const [colors, setColors] = useState<ThemeColors>(DEFAULT_COLORS)

  // Resolve theme from mode and system preference
  const resolveTheme = useCallback((themeMode: ThemeMode): 'light' | 'dark' => {
    if (themeMode === 'system') {
      return getSystemTheme()
    }
    return themeMode
  }, [])

  // Apply theme changes
  const applyTheme = useCallback(
    (newMode: ThemeMode, transition = true) => {
      const resolved = resolveTheme(newMode)

      setIsTransitioning(transition)
      applyThemeToDOM(resolved, transition)
      setResolvedTheme(resolved)

      // Update colors after DOM update
      setTimeout(
        () => {
          setColors(getThemeColors())
          setIsTransitioning(false)
        },
        transition ? 300 : 0,
      )

      // Persist to localStorage
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(THEME_STORAGE_KEY, newMode)
        } catch (error) {
          logger.warn('[ThemeProvider] Failed to save theme to localStorage:', error)
        }
      }
    },
    [resolveTheme],
  )

  // Set theme mode
  const setMode = useCallback(
    (newMode: ThemeMode) => {
      setModeState(newMode)
      applyTheme(newMode)
    },
    [applyTheme],
  )

  // Toggle between light and dark. It flips the RESOLVED theme, not the stored mode: with the
  // default 'system' mode on a light OS the old mode-based flip chose 'light' again, so the
  // "Switch to dark theme" row of the navbar did nothing (navigation journey, wave 8 checkpoint).
  const toggleTheme = useCallback(() => {
    const nextMode = resolvedTheme === 'light' ? 'dark' : 'light'
    setModeState(nextMode)
    applyTheme(nextMode)
  }, [resolvedTheme, applyTheme])

  // Set specific modes
  const setLightMode = useCallback(() => setMode('light'), [setMode])
  const setDarkMode = useCallback(() => setMode('dark'), [setMode])
  const setSystemMode = useCallback(() => setMode('system'), [setMode])

  // Initialize theme on mount
  useEffect(() => {
    const initial = initialMode ?? getInitialThemeMode()
    const resolved = resolveTheme(initial)
    applyThemeToDOM(resolved, false)
    setResolvedTheme(resolved)
    setColors(getThemeColors())
  }, [initialMode, resolveTheme])

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia || mode !== 'system') {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (event: MediaQueryListEvent) => {
      const newTheme = event.matches ? 'dark' : 'light'
      setResolvedTheme(newTheme)
      applyThemeToDOM(newTheme, true)
      setColors(getThemeColors())
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange)
      return () => mediaQuery.removeListener(handleChange)
    }
    return undefined
  }, [mode])

  // Update colors when resolved theme changes
  useEffect(() => {
    setColors(getThemeColors())
  }, [resolvedTheme])

  // Context value
  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      resolvedTheme,
      isTransitioning,
      colors,
      setMode,
      toggleTheme,
      setLightMode,
      setDarkMode,
      setSystemMode,
    }),
    [
      mode,
      resolvedTheme,
      isTransitioning,
      colors,
      setMode,
      toggleTheme,
      setLightMode,
      setDarkMode,
      setSystemMode,
    ],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/**
 * useTheme hook
 *
 * Access theme context for theme control.
 *
 * @throws {Error} If used outside of ThemeProvider
 *
 * @example
 * ```tsx
 * const { mode, resolvedTheme, toggleTheme, setDarkMode } = useTheme();
 *
 * return (
 *   <button onClick={toggleTheme}>
 *     Current: {resolvedTheme}
 *   </button>
 * );
 * ```
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext)

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }

  return context
}
